import { Badge, Button, Dropdown, Empty, List, Spin, Typography } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { useBoundStore } from "store";
import {
  useNotifications,
  useUnreadNotificationCount,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
} from "notifications/queries";
import { INotification } from "../models";
import "./NotificationBell.scss";

dayjs.extend(relativeTime);

export const NotificationBell = () => {
  const navigate = useNavigate();
  const { tenant, currentOrganization } = useBoundStore();
  const organizationId = currentOrganization?.id;

  const [open, setOpen] = useState(false);

  const { data: notifications, isLoading: loading } = useNotifications(organizationId, {
    enabled: open,
  });
  const { data: count } = useUnreadNotificationCount(organizationId, {
    refetchInterval: 30000,
  } as any);
  const { mutateAsync: markAsRead } = useMarkNotificationReadMutation();
  const { mutateAsync: markAllAsRead } = useMarkAllNotificationsReadMutation();

  const handleNotificationClick = useCallback(
    async (notification: INotification) => {
      if (!notification.read && tenant?.id && organizationId) {
        await markAsRead({
          tenantId: tenant.id,
          organizationId,
          notificationId: notification.id,
        });
      }

      // Navigate to the referenced item
      if (notification.referenceType === "note" && notification.projectId) {
        navigate(
          `/${tenant?.id}/organizations/${organizationId}/projects/${notification.projectId}`
        );
      }

      setOpen(false);
    },
    [markAsRead, tenant?.id, organizationId, navigate]
  );

  const handleMarkAllRead = useCallback(async () => {
    if (tenant?.id && organizationId) {
      await markAllAsRead({
        tenantId: tenant.id,
        organizationId,
      });
    }
  }, [tenant?.id, organizationId, markAllAsRead]);

  const dropdownContent = (
    <div className="notification-dropdown">
      <div className="notification-dropdown-header">
        <Typography.Title level={5} style={{ margin: 0 }}>
          Notifications
        </Typography.Title>
        {(count ?? 0) > 0 && (
          <Button type="link" size="small" onClick={handleMarkAllRead}>
            Mark all as read
          </Button>
        )}
      </div>
      <div className="notification-dropdown-content">
        {loading ? (
          <div className="notification-loading">
            <Spin size="small" />
          </div>
        ) : !notifications || notifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No notifications"
          />
        ) : (
          <List
            dataSource={notifications}
            renderItem={(notification) => (
              <List.Item
                className={`notification-item ${
                  !notification.read ? "notification-item--unread" : ""
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <List.Item.Meta
                  title={notification.title}
                  description={
                    <>
                      {notification.message && (
                        <Typography.Text
                          type="secondary"
                          style={{ display: "block", fontSize: 12 }}
                        >
                          {notification.message}
                        </Typography.Text>
                      )}
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 11 }}
                      >
                        {dayjs(notification.createdAt).fromNow()}
                      </Typography.Text>
                    </>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={["click"]}
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
    >
      <Badge count={count ?? 0} size="small" offset={[-2, 2]}>
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: 20 }} />}
          className="notification-bell-button"
        />
      </Badge>
    </Dropdown>
  );
};
